window.LogicCraft = window.LogicCraft || {};

window.LogicCraft.challenges = (function() {

    const PHASES = [
        { id: 'fase-1', name: 'Fase 1', icon: '⚡' },
        { id: 'fase-2', name: 'Fase 2', icon: '🔀' },
        { id: 'fase-3', name: 'Fase 3', icon: '💡' },
        { id: 'fase-4', name: 'Fase 4', icon: '🔢' },
        { id: 'fase-5', name: 'Fase 5', icon: '🛡️' },
        { id: 'fase-6', name: 'Fase 6', icon: '🛑' },
        { id: 'fase-7', name: 'Fase 7', icon: '⏱️' },
        { id: 'fase-8', name: 'Fase 8', icon: '🌊' },
        { id: 'fase-9', name: 'Fase 9', icon: '⚙️' },
        { id: 'fase-10', name: 'Fase 10', icon: '📟' }
    ];

    // Funções auxiliares de checagem estrutural de conexões (Jumper ou Trilha PCB)
    function isNodePinConnected(wires, node, targetPinIds, nodesMap) {
        if (!node) return false;
        // 1. Conexão via Cabo Aéreo (Jumper Banana)
        if (wires && Array.isArray(wires)) {
            const hasAir = wires.some(w => {
                const fromMatch = (w.from && Math.abs(w.from.nodeX - node.x) < 0.25 && Math.abs(w.from.nodeZ - node.z) < 0.25 && 
                                   (!targetPinIds || targetPinIds.includes(w.from.pinId)));
                const toMatch = (w.to && Math.abs(w.to.nodeX - node.x) < 0.25 && Math.abs(w.to.nodeZ - node.z) < 0.25 && 
                                 (!targetPinIds || targetPinIds.includes(w.to.pinId)));
                return fromMatch || toMatch;
            });
            if (hasAir) return true;
        }

        // 2. Conexão via Trilha PCB (fio terrestre COMP.WIRE ou JUMPER na malha)
        const circuit = nodesMap || (typeof window !== 'undefined' ? window.LogicCraft?.state?.circuit : null);
        if (circuit) {
            const core = (typeof window !== 'undefined' ? window.LogicCraft?.core : null);
            const pins = core ? core.getComponentPins(node) : null;
            const allPins = pins ? [...pins.inputs, ...pins.outputs] : [];
            const checkPins = targetPinIds ? allPins.filter(p => targetPinIds.includes(p.id) || (p.aliases && p.aliases.some(a => targetPinIds.includes(a)))) : allPins;

            for (const p of checkPins) {
                if (core) {
                    const direct = circuit.get(core.key(p.x, 0, p.z));
                    if (direct && (direct.type === 'WIRE' || direct.type === 'JUMPER')) return true;
                }
                for (const n of circuit.values()) {
                    if ((n.type === 'WIRE' || n.type === 'JUMPER') && Math.hypot(n.x - p.x, n.z - p.z) <= 0.35) return true;
                }
            }

            // Checa adjacência imediata do corpo do componente na grade (dx/dz = 0.5)
            const neighbors = [[0, 0.5], [0, -0.5], [0.5, 0], [-0.5, 0]];
            for (const [dx, dz] of neighbors) {
                if (core) {
                    const nb = circuit.get(core.key(node.x + dx, 0, node.z + dz));
                    if (nb && (nb.type === 'WIRE' || nb.type === 'JUMPER')) return true;
                }
                for (const n of circuit.values()) {
                    if ((n.type === 'WIRE' || n.type === 'JUMPER') && Math.hypot(n.x - (node.x + dx), n.z - (node.z + dz)) <= 0.25) return true;
                }
            }
        }

        return false;
    }

    function countTotalConductors(wires, nodesMap) {
        let count = (wires && Array.isArray(wires)) ? wires.length : 0;
        const circuit = nodesMap || (typeof window !== 'undefined' ? window.LogicCraft?.state?.circuit : null);
        if (circuit) {
            for (const n of circuit.values()) {
                if (n.type === 'WIRE' || n.type === 'JUMPER') count++;
            }
        }
        return count;
    }

    function isNodeHoseConnected(hoses, node, targetPortIds) {
        if (!hoses || !node) return false;
        return hoses.some(h => {
            const fromMatch = (h.from && h.from.nodeX === node.x && h.from.nodeZ === node.z && 
                               (!targetPortIds || targetPortIds.includes(h.from.portId)));
            const toMatch = (h.to && h.to.nodeX === node.x && h.to.nodeZ === node.z && 
                             (!targetPortIds || targetPortIds.includes(h.to.portId)));
            return fromMatch || toMatch;
        });
    }

    function testFunctionalLogicVectors(keys, outputNode, vectors) {
        const sim = window.LogicCraft?.simulation;
        if (!sim || typeof sim.propagateSignals !== 'function') {
            return { ok: true };
        }
        const originalStates = keys.map(k => ({ node: k, val: k.manualValue }));
        try {
            for (const vec of vectors) {
                vec.inputs.forEach((val, idx) => {
                    if (keys[idx]) keys[idx].manualValue = Boolean(val);
                });
                sim.propagateSignals();
                const actualOut = Boolean(outputNode.state);
                if (actualOut !== Boolean(vec.expected)) {
                    const inputStr = vec.inputs.map((v, i) => `${keys[i]?.switchId || String.fromCharCode(65 + i)}=${v ? 1 : 0}`).join(', ');
                    return {
                        ok: false,
                        msg: `Teste funcional falhou para ${inputStr}: saída esperada ${vec.expected ? 1 : 0}, saída obtida ${actualOut ? 1 : 0}. Verifique suas conexões.`
                    };
                }
            }
            return { ok: true };
        } finally {
            originalStates.forEach(s => { s.node.manualValue = s.val; });
            sim.propagateSignals();
        }
    }

    const CHALLENGES = {
        'fase-1': [
            {
                id: 'ch-1',
                phase: 1,
                title: 'Monte a lógica do LED',
                objective: 'Faça o LED acender quando A e B estiverem ligados, ou quando C estiver desligado. Expressão: S = (A · B) + ¬C.',
                theory: 'Expressões booleanas combinam variáveis lógicas usando operadores AND (multiplicação), OR (soma) e NOT (inversão). Neste desafio, a saída deve ser nível lógico 1 se o termo A·B for verdadeiro, ou se C for 0 (fazendo ¬C = 1).',
                steps: [
                    '1. Insira 3 chaves na bancada (A, B e C).',
                    '2. Conecte as saídas de A e B nas entradas da porta AND.',
                    '3. Conecte a saída de C na entrada da porta NOT.',
                    '4. Conecte as saídas da porta AND e da porta NOT nas entradas da porta OR.',
                    '5. Conecte a saída da porta OR no terminal do LED ou Lâmpada.'
                ],
                hint: 'Passe o sinal de C pela porta NOT antes de conectar à porta OR.',
                quiz: {
                    question: 'Para a saída S ser nível lógico 1, qual das combinações de entrada é válida?',
                    options: ['A=0, B=0, C=1', 'A=1, B=1, C=0', 'A=1, B=0, C=1', 'A=0, B=1, C=1'],
                    correct: 1
                },
                verifyCircuit: function(nodes, wires, hoses) {
                    const keys = Array.from(nodes.values()).filter(n => n.type === 'SWITCH');
                    if (keys.length < 3) return { ok: false, msg: 'Insira 3 Chaves Lógicas (A, B, C).' };
                    const andNode = Array.from(nodes.values()).find(n => n.type === 'AND');
                    if (!andNode) return { ok: false, msg: 'Insira uma Porta AND.' };
                    const orNode = Array.from(nodes.values()).find(n => n.type === 'OR');
                    if (!orNode) return { ok: false, msg: 'Insira uma Porta OR.' };
                    const notNode = Array.from(nodes.values()).find(n => n.type === 'NOT');
                    if (!notNode) return { ok: false, msg: 'Insira uma Porta NOT.' };
                    const ledNode = Array.from(nodes.values()).find(n => n.type === 'LED_RED' || n.type === 'LAMP');
                    if (!ledNode) return { ok: false, msg: 'Insira um LED ou Lâmpada indicadora.' };

                    // Verificação de conexões físicas mínimas (Trilhas PCB ou Jumpers)
                    if (countTotalConductors(wires, nodes) < 5) {
                        return { ok: false, msg: 'Conecte todos os fios entre as chaves, portas e o LED (mínimo de 5 condutores, via trilha ou jumper).' };
                    }
                    if (!isNodePinConnected(wires, ledNode, ['X1', 'A1', '1', 'IN'], nodes)) {
                        return { ok: false, msg: 'Conecte a saída da porta OR à entrada do LED ou Lâmpada.' };
                    }
                    if (!isNodePinConnected(wires, notNode, null, nodes)) {
                        return { ok: false, msg: 'Conecte a chave C na entrada da porta NOT e a saída do NOT na porta OR.' };
                    }
                    if (!isNodePinConnected(wires, andNode, null, nodes)) {
                        return { ok: false, msg: 'Conecte as chaves A e B nas entradas da porta AND e a saída do AND na porta OR.' };
                    }
                    if (!isNodePinConnected(wires, orNode, null, nodes)) {
                        return { ok: false, msg: 'Conecte as saídas da porta AND e NOT nas entradas da porta OR.' };
                    }
                    for (const k of keys.slice(0, 3)) {
                        if (!isNodePinConnected(wires, k, null, nodes)) {
                            return { ok: false, msg: `Conecte o terminal da chave ${k.switchId || 'lógica'} ao circuito.` };
                        }
                    }

                    // Teste funcional por vetores booleanos
                    const keyA = keys.find(k => k.switchId === 'A') || keys[0];
                    const keyB = keys.find(k => k.switchId === 'B') || keys[1];
                    const keyC = keys.find(k => k.switchId === 'C') || keys[2];

                    const testRes = testFunctionalLogicVectors([keyA, keyB, keyC], ledNode, [
                        { inputs: [0, 0, 1], expected: 0 },
                        { inputs: [0, 0, 0], expected: 1 },
                        { inputs: [1, 1, 1], expected: 1 },
                        { inputs: [1, 0, 1], expected: 0 }
                    ]);
                    if (!testRes.ok) return testRes;

                    return { ok: true, msg: 'Montagem aprovada nos critérios deste desafio. Todos os testes de entrada responderam corretamente.' };
                }
            }
        ],
        'fase-2': [
            {
                id: 'ch-2',
                phase: 2,
                title: 'Equivalência de De Morgan',
                objective: 'Monte a lógica equivalente a uma porta NAND usando apenas inversores (NOT) e uma porta OR para acionar o LED. A luz só deve apagar quando A=1 e B=1.',
                theory: 'O Teorema de De Morgan prova que a negação de um produto booleano equivale à soma das variáveis negadas: ¬(A · B) ≡ ¬A + ¬B. A luz permanece acesa para quase todas as combinações, apagando exclusivamente quando ambas as chaves forem 1.',
                steps: [
                    '1. Insira 2 chaves lógicas (A e B).',
                    '2. Conecte cada chave em uma porta NOT individual.',
                    '3. Pegue as saídas invertidas e ligue-as às entradas de uma porta OR.',
                    '4. Ligue a saída da porta OR na entrada de um LED.',
                    '5. Teste o funcionamento: o LED só apaga quando A=1 e B=1.'
                ],
                hint: 'Não utilize a porta NAND. Você deve provar o teorema utilizando apenas portas NOT e uma porta OR.',
                quiz: {
                    question: 'Qual a representação de De Morgan para a porta NOR (¬(A + B))?',
                    options: ['¬A + ¬B', 'A · B', '¬A · ¬B', 'A + B'],
                    correct: 2
                },
                verifyCircuit: function(nodes, wires, hoses) {
                    const keys = Array.from(nodes.values()).filter(n => n.type === 'SWITCH');
                    if (keys.length < 2) return { ok: false, msg: 'Insira 2 Chaves Lógicas (A e B).' };
                    const nandNode = Array.from(nodes.values()).find(n => n.type === 'NAND');
                    if (nandNode) return { ok: false, msg: 'Remova a porta NAND. O objetivo é provar o teorema utilizando apenas NOT e OR.' };
                    const notNodes = Array.from(nodes.values()).filter(n => n.type === 'NOT');
                    if (notNodes.length < 2) return { ok: false, msg: 'Insira pelo menos 2 Portas NOT.' };
                    const orNode = Array.from(nodes.values()).find(n => n.type === 'OR');
                    if (!orNode) return { ok: false, msg: 'Insira uma Porta OR.' };
                    const ledNode = Array.from(nodes.values()).find(n => n.type === 'LED_RED' || n.type === 'LAMP');
                    if (!ledNode) return { ok: false, msg: 'Insira um LED ou Lâmpada para sinalizar a saída.' };

                    // Verificação de conexões físicas (Trilhas PCB ou Jumpers)
                    if (countTotalConductors(wires, nodes) < 5) {
                        return { ok: false, msg: 'Conecte todos os fios entre as chaves, as portas NOT, OR e o LED (mínimo de 5 condutores, via trilha ou jumper).' };
                    }
                    if (!isNodePinConnected(wires, ledNode, ['X1', 'A1', '1', 'IN'], nodes)) {
                        return { ok: false, msg: 'Conecte a saída da porta OR na entrada do LED.' };
                    }
                    if (!isNodePinConnected(wires, orNode, null, nodes)) {
                        return { ok: false, msg: 'Conecte as saídas das portas NOT nas entradas da porta OR.' };
                    }
                    if (!notNodes.every(n => isNodePinConnected(wires, n, null, nodes))) {
                        return { ok: false, msg: 'Conecte cada uma das portas NOT nas chaves e na porta OR.' };
                    }
                    for (const k of keys.slice(0, 2)) {
                        if (!isNodePinConnected(wires, k, null, nodes)) {
                            return { ok: false, msg: `Conecte a saída da chave ${k.switchId || 'lógica'} à sua porta NOT.` };
                        }
                    }

                    // Teste funcional por vetores booleanos
                    const keyA = keys.find(k => k.switchId === 'A') || keys[0];
                    const keyB = keys.find(k => k.switchId === 'B') || keys[1];

                    const testRes = testFunctionalLogicVectors([keyA, keyB], ledNode, [
                        { inputs: [0, 0], expected: 1 },
                        { inputs: [0, 1], expected: 1 },
                        { inputs: [1, 0], expected: 1 },
                        { inputs: [1, 1], expected: 0 }
                    ]);
                    if (!testRes.ok) return testRes;

                    return { ok: true, msg: 'Montagem aprovada nos critérios deste desafio. De Morgan demonstrado com sucesso!' };
                }
            }
        ],
        'fase-3': [
            {
                id: 'ch-3',
                phase: 3,
                title: 'Lógica do Segmento "a" do Display',
                objective: 'Monte a lógica combinacional para o segmento superior "a" do display usando as chaves A, B, C, D e ligue a saída diretamente no terminal "a" do Display de 7 segmentos. Expressão: Sa = A + C + (B · D) + (¬B · ¬D).',
                theory: 'No código BCD (A=8, B=4, C=2, D=1), o traço horizontal superior "a" deve acender nos dígitos 0, 2, 3, 5, 7, 8 e 9, permanecendo apagado em 1 e 4.',
                steps: [
                    '1. Insira 4 chaves (A=Bit 3/Peso 8 até D=Bit 0/Peso 1).',
                    '2. Crie o ramo (B · D) usando uma porta AND.',
                    '3. Crie o ramo (¬B · ¬D) invertendo B e D com portas NOT e multiplicando com outra porta AND.',
                    '4. Some os termos: A + C + (B · D) + (¬B · ¬D) utilizando portas OR em cascata.',
                    '5. Conecte o sinal final diretamente no pino "a" do Display de 7 segmentos.'
                ],
                hint: 'Para somar 4 termos, encadeie as saídas das portas OR em cascata.',
                quiz: {
                    question: 'No dígito BCD 0001 (Decimal 1), o segmento "a" deve acender?',
                    options: ['Sim, pois a equação tem o termo A.', 'Não, o dígito 1 é formado apenas pelos segmentos verticais direitos (b e c).', 'Sim, por causa do termo C.', 'Não, o número 1 acende todos os segmentos.'],
                    correct: 1
                },
                verifyCircuit: function(nodes, wires, hoses) {
                    const keys = Array.from(nodes.values()).filter(n => n.type === 'SWITCH');
                    if (keys.length < 4) return { ok: false, msg: 'Insira 4 Chaves (A, B, C, D).' };
                    const display = Array.from(nodes.values()).find(n => n.type === 'DISPLAY_7SEG');
                    if (!display) return { ok: false, msg: 'Insira o Display de 7 Segmentos receptor.' };
                    const orNodes = Array.from(nodes.values()).filter(n => n.type === 'OR');
                    if (orNodes.length < 1) return { ok: false, msg: 'Você precisará de portas OR para a soma dos termos.' };
                    
                    if (countTotalConductors(wires, nodes) < 4) {
                        return { ok: false, msg: 'Conecte os fios da lógica entre as chaves, portas e o Display (via trilha ou jumper).' };
                    }

                    const hasPinA = isNodePinConnected(wires, display, ['a'], nodes);
                    if (!hasPinA) {
                        return { ok: false, msg: 'A saída final da sua lógica OR deve ser conectada fisicamente ao pino "a" do Display de 7 Segmentos.' };
                    }

                    return { ok: true, msg: 'Montagem aprovada nos critérios deste desafio. Lógica do segmento "a" validada com sucesso.' };
                }
            }
        ],
        'fase-4': [
            {
                id: 'ch-4',
                phase: 4,
                title: 'Decodificador BCD no Display',
                objective: 'Conectar as 4 chaves (A, B, C, D) às entradas do Decodificador BCD e traçar os 7 condutores paralelos ligando as saídas "a" até "g" aos terminais correspondentes do Display de 7 segmentos.',
                theory: 'O decodificador BCD converte 4 bits binários (A=8, B=4, C=2, D=1) nos 7 sinais necessários para desenhar os algarismos decimais no mostrador.',
                steps: [
                    '1. Posicione 4 chaves em linha (A=8, B=4, C=2, D=1).',
                    '2. Insira o Decodificador BCD.',
                    '3. Conecte as chaves nas entradas A, B, C, D do decodificador.',
                    '4. Insira o Display de 7 segmentos ao lado.',
                    '5. Puxe os 7 fios ligando cada saída (a, b, c, d, e, f, g) do decodificador para as entradas correspondentes do display.'
                ],
                hint: 'Ligue cada saída no terminal correspondente do display: a com a, b com b, e assim sucessivamente.',
                quiz: {
                    question: 'Quantas portas lógicas internas aproximadamente um Decodificador BCD substitui?',
                    options: ['Cerca de 4 portas.', 'Apenas 7 portas.', 'Mais de 30 portas lógicas.', 'Nenhuma, ele é apenas uma chave mecânica.'],
                    correct: 2
                },
                verifyCircuit: function(nodes, wires, hoses) {
                    const ci = Array.from(nodes.values()).find(n => n.type === 'DEC_BCD_7SEG');
                    if (!ci) return { ok: false, msg: 'Insira o Decodificador BCD.' };
                    const display = Array.from(nodes.values()).find(n => n.type === 'DISPLAY_7SEG');
                    if (!display) return { ok: false, msg: 'Insira o Display de 7 Segmentos receptor.' };
                    
                    if (countTotalConductors(wires, nodes) < 7) {
                        return { ok: false, msg: 'Conecte os fios do barramento entre o Decodificador BCD e o Display (via trilha ou jumper).' };
                    }

                    let wireCount = 0;
                    ['a','b','c','d','e','f','g'].forEach(seg => {
                        if (isNodePinConnected(wires, display, [seg], nodes)) wireCount++;
                    });
                    
                    if (wireCount < 7) {
                        return { ok: false, msg: `Conecte os 7 condutores de 'a' até 'g' do decodificador ao display. Encontrados: ${wireCount}/7.` };
                    }
                    
                    return { ok: true, msg: 'Montagem aprovada nos critérios deste desafio. Barramento BCD conectado com sucesso.' };
                }
            }
        ],
        'fase-5': [
            {
                id: 'ch-5',
                phase: 5,
                title: 'Comando Bimanual Pneumático',
                objective: 'Alimente a válvula 5/2 com 6 bar da FRL. Use uma porta AND para exigir que as duas chaves (A e B) estejam ligadas para energizar o solenoide Y1 e avançar o cilindro de dupla ação.',
                theory: 'O comando bimanual exige que o operador mantenha ambas as mãos nas chaves de comando simultaneamente, prevenindo o avanço acidental do cilindro durante a operação.',
                steps: [
                    '1. Ligue a FRL clicando nela para aplicar pressão de 6 bar.',
                    '2. Conecte duas chaves lógicas a uma porta AND.',
                    '3. Conecte a saída do AND ao solenoide elétrico Y1 da Válvula 5/2.',
                    '4. Ligue a mangueira do pórtico P da FRL ao pórtico P da Válvula 5/2.',
                    '5. Conecte os pórticos A e B da válvula às conexões de avanço e recuo do cilindro.'
                ],
                hint: 'Para que o cilindro avance, a válvula precisa de comando elétrico E suprimento de pressão simultâneos.',
                quiz: {
                    question: 'Qual norma regulamentadora brasileira aborda comandos bimanuais de segurança em máquinas e equipamentos?',
                    options: ['NR-10', 'NR-12', 'ISO 1219', 'NR-35'],
                    correct: 1
                },
                verifyCircuit: function(nodes, wires, hoses) {
                    const pneu = Array.from(nodes.values()).find(n => n.type === 'SUPPLY_PNEU');
                    if (!pneu) return { ok: false, msg: 'Insira a Unidade FRL.' };
                    if (pneu.manualValue === false) return { ok: false, msg: 'Ligue a FRL clicando nela para aplicar pressão de 6 bar na rede pneumática.' };
                    
                    const andNode = Array.from(nodes.values()).find(n => n.type === 'AND');
                    if (!andNode) return { ok: false, msg: 'Insira a Porta AND para garantir o comando bimanual.' };
                    
                    const valve = Array.from(nodes.values()).find(n => n.type === 'VALVE_5_2_MONO' || n.type === 'VALVE_5_2_BI');
                    if (!valve) return { ok: false, msg: 'Insira a Válvula 5/2.' };
                    
                    const cyl = Array.from(nodes.values()).find(n => n.type === 'CYL_DOUBLE');
                    if (!cyl) return { ok: false, msg: 'Insira o Cilindro pneumático de Dupla Ação.' };
                    
                    if (!hoses || hoses.length < 3) {
                        return { ok: false, msg: 'Conecte as mangueiras: FRL(P) -> Válvula(P) e Válvula(A,B) -> Cilindro.' };
                    }

                    if (countTotalConductors(wires, nodes) < 2) {
                        return { ok: false, msg: 'Conecte os fios elétricos das chaves até a porta AND e da porta AND até o solenoide Y1 da válvula (via trilha ou jumper).' };
                    }

                    const valveWired = isNodePinConnected(wires, valve, ['Y1', 'A1', 'YH1'], nodes);
                    if (!valveWired) {
                        return { ok: false, msg: 'Conecte a saída da porta AND ao solenoide elétrico Y1 da válvula.' };
                    }
                    
                    return { ok: true, msg: 'Montagem aprovada nos critérios deste desafio. Comando bimanual pneumático integrado com sucesso.' };
                }
            }
        ],
        'fase-6': [
            {
                id: 'ch-6',
                phase: 6,
                title: 'Partida com Homing de Segurança',
                objective: 'Crie um circuito onde o comando de avanço da válvula só recebe nível lógico 1 se a chave de Partida estiver ligada E o sensor de recuo S0 do cilindro estiver ativo (êmbolo na posição de repouso).',
                theory: 'O Homing confirma que o atuador está na posição de recuo segura antes de iniciar o movimento, evitando partidas com peças presas ou desalinhadas.',
                steps: [
                    '1. Ligue a FRL, monte a Válvula 5/2 e o Cilindro com suas mangueiras.',
                    '2. Conecte a Chave de Partida em uma entrada da porta AND.',
                    '3. Conecte o pino do sensor S0 (Sensor de Recuo) do Cilindro na outra entrada da porta AND.',
                    '4. Ligue a saída da porta AND na bobina da válvula pneumática.',
                    '5. Teste o acionamento: o cilindro só avança se estiver recuado na partida.'
                ],
                hint: 'Lógica: Avanço = Partida · S0. Se S0 = 0 (cilindro avançado), a partida fica inoperante.',
                quiz: {
                    question: 'Se a máquina parou no meio do caminho com o cilindro avançado (S0 = 0), o que ocorre ao acionar Partida sob esta lógica?',
                    options: ['O cilindro recua automaticamente.', 'A válvula avança com mais pressão.', 'Nada acontece, pois a porta AND bloqueia a Partida enquanto S0=0.', 'Ocorrerá um curto-circuito lógico.'],
                    correct: 2
                },
                verifyCircuit: function(nodes, wires, hoses) {
                    const andNode = Array.from(nodes.values()).find(n => n.type === 'AND');
                    if (!andNode) return { ok: false, msg: 'O intertravamento de Homing requer uma porta AND (Partida · S0).' };
                    const cyl = Array.from(nodes.values()).find(n => n.type === 'CYL_DOUBLE' || n.type === 'CYL_SINGLE');
                    if (!cyl) return { ok: false, msg: 'Insira um Cilindro Pneumático com sensores S0/S1.' };
                    const valve = Array.from(nodes.values()).find(n => n.type === 'VALVE_5_2_MONO' || n.type === 'VALVE_5_2_BI' || n.type === 'VALVE_3_2_MONO');
                    if (!valve) return { ok: false, msg: 'Insira a Válvula direcional.' };

                    if (countTotalConductors(wires, nodes) < 2) {
                        return { ok: false, msg: 'Conecte os fios da chave de partida, do sensor S0 e da válvula na porta AND (via trilha ou jumper).' };
                    }
                    
                    const hasS0Wire = isNodePinConnected(wires, cyl, ['S0'], nodes);
                    if (!hasS0Wire) {
                        return { ok: false, msg: 'Conecte o pino elétrico do sensor S0 do cilindro na entrada da sua porta AND.' };
                    }

                    const hasValveWire = isNodePinConnected(wires, valve, ['Y1', 'A1', 'YH1'], nodes);
                    if (!hasValveWire) {
                        return { ok: false, msg: 'Conecte a saída da porta AND ao solenoide da válvula.' };
                    }

                    return { ok: true, msg: 'Montagem aprovada nos critérios deste desafio. Intertravamento de Homing validado com sucesso.' };
                }
            }
        ],
        'fase-7': [
            {
                id: 'ch-7',
                phase: 7,
                title: 'Controle de Velocidade e Alarme de Tempo',
                objective: 'Conectar a Reguladora de Fluxo em linha com o cilindro e ajustar a abertura para 25% (ou 10%). Conectar um temporizador TON (2.0s) e fazer o Buzzer soar se o sensor S1 não for atingido a tempo: Alarme = TON.Q · ¬S1.',
                theory: 'A reguladora estrangula o fluxo de ar para reduzir a velocidade do pistão. O temporizador TON monitora o tempo de percurso: se o sensor de avanço S1 demorar mais que o tempo programado para atuar, o circuito reconhece falha mecânica e dispara o alarme.',
                steps: [
                    '1. Adicione a Reguladora de Fluxo e clique nela para ajustar para 25% (ou 10%).',
                    '2. Conecte a mangueira passando pela Reguladora antes de ir para o cilindro.',
                    '3. Conecte o sinal de partida na entrada do Temporizador TON.',
                    '4. O alarme deve soar se: Saída do TON está alta (Q=1) E o sensor S1 NÃO está acionado (NOT em S1).',
                    '5. Conecte o sinal do alarme ao Buzzer sonoro.'
                ],
                hint: 'Alarme = TON.Q AND NOT(S1). Conecte a saída da porta lógica no terminal do Buzzer.',
                quiz: {
                    question: 'A Reguladora de Fluxo em circuitos pneumáticos de dupla ação é idealmente configurada para controlar:',
                    options: ['A pressão principal.', 'O fluxo do ar que entra no cilindro (meter-in).', 'O fluxo do ar de escape saindo do cilindro (meter-out).', 'A voltagem do solenoide.'],
                    correct: 2
                },
                verifyCircuit: function(nodes, wires, hoses) {
                    const flowValve = Array.from(nodes.values()).find(n => n.type === 'VALVE_FLOW_CONTROL');
                    if (!flowValve) return { ok: false, msg: 'Adicione a Reguladora de Fluxo em linha.' };
                    
                    const opening = flowValve.openingPercent ?? 100;
                    if (opening === 0) {
                        return { ok: false, msg: 'A reguladora está totalmente fechada (0%). Abra para 25% ou 10% para permitir o fluxo controlado.' };
                    }
                    if (opening > 25) {
                        return { ok: false, msg: 'Ajuste a reguladora para 25% ou 10% clicando nela para reduzir a velocidade do cilindro.' };
                    }

                    const cyl = Array.from(nodes.values()).find(n => n.type === 'CYL_DOUBLE' || n.type === 'CYL_SINGLE');
                    if (!cyl) return { ok: false, msg: 'Insira o Cilindro Pneumático com sensor de avanço S1.' };
                    
                    const timer = Array.from(nodes.values()).find(n => n.type === 'TIMER_TON');
                    if (!timer) return { ok: false, msg: 'Insira o Temporizador TON para monitorar o tempo limite.' };
                    
                    const buzzer = Array.from(nodes.values()).find(n => n.type === 'BUZZER');
                    if (!buzzer) return { ok: false, msg: 'Instale o Buzzer Sonoro para o alarme.' };
                    
                    if (!hoses || hoses.length < 2 || !isNodeHoseConnected(hoses, flowValve)) {
                        return { ok: false, msg: 'Conecte as mangueiras passando pela Reguladora de Fluxo até o pórtico do cilindro.' };
                    }

                    if (countTotalConductors(wires, nodes) < 2) {
                        return { ok: false, msg: 'Conecte os fios do circuito: partida no TON, sensor S1 na lógica de alarme e saída no Buzzer (via trilha ou jumper).' };
                    }

                    if (!isNodePinConnected(wires, timer, null, nodes)) {
                        return { ok: false, msg: 'Conecte o sinal de comando na entrada do Temporizador TON.' };
                    }

                    if (!isNodePinConnected(wires, cyl, ['S1'], nodes)) {
                        return { ok: false, msg: 'Conecte o pino do sensor S1 do cilindro à lógica do alarme de tempo.' };
                    }

                    if (!isNodePinConnected(wires, buzzer, ['X1', 'A1', '1', 'IN'], nodes)) {
                        return { ok: false, msg: 'Conecte a saída do alarme à entrada do Buzzer sonoro.' };
                    }

                    return { ok: true, msg: 'Montagem aprovada nos critérios deste desafio. Controle de vazão e alarme de tempo validados.' };
                }
            }
        ],
        'fase-8': [
            {
                id: 'ch-8',
                phase: 8,
                title: 'Autorretenção e Linha de Tanque',
                objective: 'Criar circuito de selo (botoeira NA de partida e botoeira NF de parada) na bobina do Relé para acionar a Válvula 4/2 e o Cilindro Hidráulico. Conectar obrigatoriamente a mangueira do pórtico T da válvula de volta ao tanque T da unidade hidráulica.',
                theory: 'O circuito de autorretenção (selo) mantém o circuito energizado após o operador soltar o botão de partida. Na hidráulica (120 bar), a linha de retorno T para o tanque é obrigatória para evitar travamento por sobrepressão.',
                steps: [
                    '1. Ligue a Unidade Hidráulica clicando na chave rotativa (120 bar).',
                    '2. Conecte uma botoeira NF ao circuito e a botoeira NA à bobina A1 do Relé.',
                    '3. Feche o selo ligando o contato NO do relé em paralelo com a partida.',
                    '4. Ligue a saída do contato do relé à bobina da válvula hidráulica 4/2.',
                    '5. Conecte a mangueira P na válvula, as mangueiras A e B no atuador, e a mangueira T da válvula DE VOLTA ao pórtico T da unidade hidráulica.'
                ],
                hint: 'Não se esqueça de ligar a mangueira do pórtico T da válvula de volta à entrada de tanque T da unidade hidráulica.',
                quiz: {
                    question: 'No circuito de autorretenção (Selo), qual o papel da botoeira NF?',
                    options: ['Acelerar a válvula.', 'Interromper o fornecimento de energia à bobina (reset).', 'Realizar a operação E lógica (AND).', 'Ligar a bomba.'],
                    correct: 1
                },
                verifyCircuit: function(nodes, wires, hoses) {
                    const hpu = Array.from(nodes.values()).find(n => n.type === 'SUPPLY_HYDRA');
                    if (!hpu) return { ok: false, msg: 'Insira a Unidade Hidráulica.' };
                    if (hpu.manualValue === false) return { ok: false, msg: 'Ligue a unidade hidráulica clicando nela para pressurizar o óleo (120 bar).' };
                    
                    const relay = Array.from(nodes.values()).find(n => n.type === 'RELAY');
                    if (!relay) return { ok: false, msg: 'Insira o Relé para montar o circuito de selo.' };

                    const valve = Array.from(nodes.values()).find(n => n.type === 'VALVE_4_2_MONO' || n.type === 'VALVE_4_2_BI');
                    if (!valve) return { ok: false, msg: 'Insira a Válvula Hidráulica 4/2.' };

                    if (!hoses || hoses.length < 2) {
                        return { ok: false, msg: 'Conecte as mangueiras de pressão e retorno da hidráulica.' };
                    }

                    const hasTankReturn = hoses.some(h => {
                        const isValveT = (h.from.nodeX === valve.x && h.from.nodeZ === valve.z && h.from.portId === 'T') ||
                                         (h.to.nodeX === valve.x && h.to.nodeZ === valve.z && h.to.portId === 'T');
                        const isHpuT = (h.from.nodeX === hpu.x && h.from.nodeZ === hpu.z && h.from.portId === 'T') ||
                                       (h.to.nodeX === hpu.x && h.to.nodeZ === hpu.z && h.to.portId === 'T');
                        return isValveT && isHpuT;
                    });
                    if (!hasTankReturn) {
                        return { ok: false, msg: 'Conecte a mangueira de retorno do pórtico T da válvula de volta ao pórtico T do tanque da unidade hidráulica.' };
                    }

                    if (countTotalConductors(wires, nodes) < 2 || !isNodePinConnected(wires, relay, null, nodes)) {
                        return { ok: false, msg: 'Conecte os fios do circuito de selo na bobina e contatos do Relé (via trilha ou jumper).' };
                    }

                    return { ok: true, msg: 'Montagem aprovada nos critérios deste desafio. Selo hidráulico e retorno ao tanque conectados.' };
                }
            }
        ],
        'fase-9': [
            {
                id: 'ch-9',
                phase: 9,
                title: 'Comando de Motor e Aterramento',
                objective: 'Montar a lógica de permissão (Partida · ¬Térmico · ¬Parada) pilotando o Relé para acionar o Motor Elétrico, e conectar obrigatoriamente o condutor de aterramento PE à carcaça do motor.',
                theory: 'O relé de interface isola o comando digital de baixa potência do circuito de potência do motor. O aterramento PE conecta a carcaça à terra para desviar correntes de fuga e proteger os operadores.',
                steps: [
                    '1. Insira 3 chaves lógicas: Partida, Relé Térmico e Botão Parada.',
                    '2. Faça a lógica AND: a saída só liga quando Partida for 1, Térmico for 0 e Parada for 0.',
                    '3. Conecte a saída digital na bobina A1 do Relé.',
                    '4. Ligue o contato NO do relé ao comando do Motor.',
                    '5. Conecte o fio verde-amarelo do condutor PE diretamente no borne PE da carcaça do Motor.'
                ],
                hint: 'O borne PE fica na carcaça do motor. Conecte o fio terra nele para cumprir os requisitos de proteção.',
                quiz: {
                    question: 'Por que utilizamos um relé entre a lógica digital e o motor, em vez de ligar direto?',
                    options: ['Porque portas AND perdem a cor.', 'Por isolamento elétrico e capacidade de conduzir a corrente necessária.', 'Porque o motor consome apenas ar.', 'Para desacelerar o sinal.'],
                    correct: 1
                },
                verifyCircuit: function(nodes, wires, hoses) {
                    const nodeList = Array.from(nodes.values());
                    const relay = nodeList.find(n => n.type === 'RELAY' || n.type === 'RELAY_SPDT');
                    if (!relay) return { ok: false, msg: 'Insira o Relé para isolar a lógica de comando do circuito do motor.' };

                    const motor = nodeList.find(n => n.type === 'MOTOR_INDUCTION_6P' || n.type === 'MOTOR_INDUCTION_MONO');
                    if (!motor) return { ok: false, msg: 'Insira o Motor Elétrico na bancada.' };

                    if (countTotalConductors(wires, nodes) < 2) {
                        return { ok: false, msg: 'Conecte os fios de comando da lógica ao Relé e o fio de aterramento PE ao motor (via trilha ou jumper).' };
                    }

                    // Verificação do aterramento PE da carcaça
                    const isPEConnected = isNodePinConnected(wires, motor, ['PE'], nodes);
                    if (!isPEConnected && !motor.grounded) {
                        return { ok: false, msg: 'Conecte o condutor de proteção PE à carcaça do motor para evitar risco de choque elétrico.' };
                    }

                    if (!isNodePinConnected(wires, relay, null, nodes)) {
                        return { ok: false, msg: 'Conecte a saída da lógica digital aos bornes de acionamento do Relé.' };
                    }

                    return { ok: true, msg: 'Montagem aprovada nos critérios deste desafio. Comando de motor com aterramento de proteção validado.' };
                }
            }
        ],
        'fase-10': [
            {
                id: 'ch-10',
                phase: 10,
                title: 'Painel de Diagnóstico no Display',
                objective: 'Conecte o Decodificador BCD ao Display de 7 segmentos. Use o botão de emergência para injetar o código 2 (0010, ativando a entrada C de peso 2) e o alarme para injetar o código 3 (0011, ativando as entradas C e D de pesos 2 e 1). Em repouso, o display exibe 0 (0000).',
                theory: 'O display transmite diagnósticos ao operador: 0 indica operação normal, 2 indica emergência acionada e 3 indica falha. Na padronização dos bits (A=8, B=4, C=2, D=1), o dígito 2 ativa o bit C (0010) e o dígito 3 ativa os bits C e D (0011).',
                steps: [
                    '1. Monte os 7 condutores paralelos ligando o Decodificador BCD ao Display.',
                    '2. Conecte o sinal do botão de Emergência na entrada C (Bit 1 / Peso 2) do Decodificador BCD.',
                    '3. Conecte o sinal de alarme nas entradas C e D (Pesos 2 e 1 = 3) do decodificador via portas OR para forçar o código 3.',
                    '4. Em repouso, o display exibe "0". Se travar: exibe "3". Se bater na emergência: exibe "2".',
                    '5. Teste o acionamento dos botões para comprovar a indicação no mostrador.'
                ],
                hint: 'Utilize portas OR nas entradas C e D do decodificador para somar os sinais de alerta com segurança.',
                quiz: {
                    question: 'Qual a principal vantagem da codificação de alarmes em BCD (Binary-Coded Decimal)?',
                    options: ['Refrigera a máquina.', 'Possibilita acionar cilindros grandes.', 'Codifica até 10 dígitos (0 a 9) usando apenas 4 condutores de sinal.', 'Elimina a necessidade de botões de emergência.'],
                    correct: 2
                },
                verifyCircuit: function(nodes, wires, hoses) {
                    const ci = Array.from(nodes.values()).find(n => n.type === 'DEC_BCD_7SEG');
                    if (!ci) return { ok: false, msg: 'Insira o Decodificador BCD para controlar o display.' };
                    const display = Array.from(nodes.values()).find(n => n.type === 'DISPLAY_7SEG');
                    if (!display) return { ok: false, msg: 'Insira o Display de 7 Segmentos.' };
                    const emg = Array.from(nodes.values()).find(n => n.type === 'EMERGENCY_STOP');
                    if (!emg) return { ok: false, msg: 'Insira o Botão de Emergência.' };

                    if (countTotalConductors(wires, nodes) < 8) {
                        return { ok: false, msg: 'Conecte os 7 condutores do barramento ao Display e o condutor de Emergência ao Decodificador BCD (via trilha ou jumper).' };
                    }

                    // Verifica se o display tem os 7 segmentos conectados ao decodificador
                    let busWires = 0;
                    ['a','b','c','d','e','f','g'].forEach(seg => {
                        if (isNodePinConnected(wires, display, [seg], nodes)) busWires++;
                    });
                    if (busWires < 7) {
                        return { ok: false, msg: `Conecte todos os 7 condutores de 'a' até 'g' do decodificador ao Display de 7 Segmentos. Encontrados: ${busWires}/7.` };
                    }

                    // Verifica conexão de comando na emergência
                    if (!isNodePinConnected(wires, emg, null, nodes)) {
                        return { ok: false, msg: 'Conecte a saída do Botão de Emergência na entrada C (Bit 1 / Peso 2) do Decodificador BCD.' };
                    }

                    // Verifica se a emergência está conectada à entrada C (código 2 = 0010)
                    const emgToC = (wires && wires.some(w => {
                        const touchesEmg = (w.from.nodeX === emg.x && w.from.nodeZ === emg.z) || (w.to.nodeX === emg.x && w.to.nodeZ === emg.z);
                        const touchesDecC = (w.from.nodeX === ci.x && w.from.nodeZ === ci.z && w.from.pinId === 'C') ||
                                            (w.to.nodeX === ci.x && w.to.nodeZ === ci.z && w.to.pinId === 'C');
                        return touchesEmg && touchesDecC;
                    })) || isNodePinConnected(wires, ci, ['C'], nodes);

                    if (!emgToC) {
                        return { ok: false, msg: 'Conecte o sinal do Botão de Emergência à entrada C (Bit 1 / Peso 2) do Decodificador BCD para gerar o código decimal 2 no display.' };
                    }

                    return { ok: true, msg: 'Montagem aprovada nos critérios deste desafio! Painel de diagnóstico integrado com sucesso.' };
                }
            }
        ]
    };

    function getPhases() {
        return PHASES;
    }

    function getChallengesForPhase(phaseId) {
        if (!CHALLENGES[phaseId]) return CHALLENGES['fase-1'];
        return CHALLENGES[phaseId];
    }

    return {
        getPhases,
        getChallengesForPhase,
        CHALLENGES
    };
})();
